import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation, useSearch } from "wouter";
import {
  Search as SearchIcon,
  Loader2,
  Sparkles,
  X,
  Trash2,
  Pin,
  PinOff,
  ArrowLeft,
  CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NoteOverlay } from "@/components/note-overlay";
import {
  useListNotes,
  useListTasks,
  useSearchNotes,
  useDeleteNote,
  useTogglePinNote,
  getSearchNotesQueryKey,
  getListNotesQueryKey,
  type Note,
} from "@workspace/api-client-react";
import { format } from "date-fns";
import { stripHtml } from "@/lib/strip-html";

export function SearchPage() {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { id } = useParams<{ id?: string }>();
  const urlSearch = useSearch();
  const [searchQuery, setSearchQuery] = React.useState(
    () => new URLSearchParams(urlSearch).get("q") ?? "",
  );
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const q = new URLSearchParams(urlSearch).get("q");
    if (q) setSearchQuery(q);
  }, [urlSearch]);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const { data: allNotes = [] } = useListNotes();
  const { data: allTasks = [] } = useListTasks();
  const taskResults = React.useMemo(() => {
    if (!searchQuery) return [];
    const live = allTasks.filter((t) => !t.deletedAt && !t.archivedAt);
    const words = searchQuery.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((w) => w.length > 1);
    if (words.length === 0) {
      const q = searchQuery.toLowerCase().trim();
      return q ? live.filter((t) => (t.title ?? "").toLowerCase().includes(q)) : [];
    }
    const need = Math.max(1, Math.ceil(words.length / 2));
    return live
      .map((t) => {
        const title = (t.title ?? "").toLowerCase();
        const score = words.filter((w) => title.includes(w) || (w.length > 3 && w.endsWith("s") && title.includes(w.slice(0, -1)))).length;
        return { t, score };
      })
      .filter((x) => x.score >= need)
      .sort((x, y) => y.score - x.score)
      .map((x) => x.t);
  }, [allTasks, searchQuery]);

  const useAISearch = searchQuery.length > 2;

  const { data: searchResults = [], isFetching: isSearching } = useSearchNotes(
    { q: searchQuery },
    {
      query: {
        enabled: useAISearch,
        queryKey: getSearchNotesQueryKey({ q: searchQuery }),
      },
    },
  );

  const basicSearchResults = React.useMemo(() => {
    if (!searchQuery || useAISearch) return [];
    const q = searchQuery.toLowerCase();
    return allNotes.filter(
      (note) =>
        note.title?.toLowerCase().includes(q) ||
        note.content?.toLowerCase().includes(q),
    );
  }, [allNotes, searchQuery, useAISearch]);

  const isSearchActive = searchQuery.length > 0;
  const notes = !isSearchActive
    ? []
    : useAISearch
      ? searchResults
      : basicSearchResults;

  const deleteNote = useDeleteNote();
  const togglePin = useTogglePinNote();

  const handleTogglePin = (id: number) => {
    togglePin.mutate(
      { id },
      {
        onSuccess: (updated) => {
          const updateList = (list: Note[] | undefined) =>
            list?.map((n) =>
              n.id === id ? { ...n, pinned: updated.pinned } : n,
            );
          queryClient.setQueryData<Note[]>(getListNotesQueryKey(), updateList);
          queryClient.setQueryData<Note[]>(
            getSearchNotesQueryKey({ q: searchQuery }),
            updateList,
          );
        },
      },
    );
  };

  const handleDelete = (id: number) => {
    deleteNote.mutate(
      { id },
      {
        onSuccess: () => {
          const remove = (list: Note[] | undefined) =>
            list?.filter((n) => n.id !== id);
          queryClient.setQueryData<Note[]>(getListNotesQueryKey(), remove);
          queryClient.setQueryData<Note[]>(
            getSearchNotesQueryKey({ q: searchQuery }),
            remove,
          );
        },
      },
    );
  };

  const openNoteId = id ? Number(id) : null;
  const openNote =
    openNoteId != null ? notes.find((n) => n.id === openNoteId) : undefined;

  const handleOpen = (note: Note) => setLocation(`/search/${note.id}`);
  const handleCloseOverlay = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation("/search");
    }
  };

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-4rem)] flex flex-col px-4 relative">
      <div className="pt-4 pb-4 shrink-0 z-10 sticky top-0 bg-background/90 backdrop-blur">
        <div className="relative flex items-center rounded-full bg-muted/50 border border-transparent focus-within:border-cyan-400/50 focus-within:ring-2 focus-within:ring-cyan-400/20 h-12 transition-colors">
          <button
            type="button"
            onClick={() => {
              if (window.history.length > 1) {
                window.history.back();
              } else {
                setLocation("/");
              }
            }}
            className="pl-4 pr-2 h-full flex items-center shrink-0 text-foreground"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search notes and tasks"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 !border-0 !shadow-none !ring-0 bg-transparent pl-0 pr-10 h-full focus-visible:ring-0"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => setSearchQuery("")}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        {isSearchActive && (
          <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5 px-1">
            {isSearching ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Searching your notes contextually...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-3 h-3 text-primary" />
                <span>
                  Found {notes.length + taskResults.length}{" "}
                  {notes.length + taskResults.length === 1 ? "result" : "results"}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 -mx-4 px-4 pb-6">
        <div className="flex flex-col gap-3 py-2">
          {!isSearchActive ? (
            <div className="flex flex-col items-center justify-center text-center text-muted-foreground gap-4 py-40">
              <SearchIcon className="h-16 w-16 stroke-[1.5] opacity-40" />
              <span>Type something or note title to search</span>
            </div>
          ) : notes.length === 0 && taskResults.length === 0 ? (
            <div className="text-center text-muted-foreground py-20">
              No memories match your search.
            </div>
          ) : (
            <>{notes.map((note) => (
              <div
                key={note.id}
                role="button"
                tabIndex={0}
                onClick={() => handleOpen(note)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleOpen(note);
                }}
                className="group relative bg-card border rounded-2xl px-5 py-4 shadow-sm animate-in fade-in slide-in-from-bottom-1 duration-300 cursor-pointer hover:border-primary/40 transition-colors"
              >
                {note.title && (
                  <p className="text-[15px] font-semibold leading-relaxed mb-0.5 truncate">
                    {note.title}
                  </p>
                )}
                <p className="text-[15px] leading-relaxed text-muted-foreground line-clamp-2 whitespace-pre-wrap">
                  {stripHtml(note.content)}
                </p>
                <span className="block text-[11px] text-muted-foreground/60 font-medium mt-2">
                  {format(new Date(note.createdAt), "MMM d, yyyy · h:mm a")}
                </span>
              </div>
            ))}
{taskResults.length > 0 && (<>
<p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1 pt-2">Tasks</p>
{taskResults.map((task) => (
<div key={"task-" + task.id} role="button" tabIndex={0} onClick={() => setLocation("/tasks/" + task.id)} onKeyDown={(e) => { if (e.key === "Enter") setLocation("/tasks/" + task.id); }} className="group relative bg-card border rounded-2xl px-5 py-4 shadow-sm cursor-pointer hover:border-primary/40 transition-colors flex items-center gap-3">
<CheckCircle2 className={"w-5 h-5 shrink-0 " + (task.completed ? "text-primary" : "text-muted-foreground/50")} />
<div className="min-w-0 flex-1">
<p className={"text-[15px] font-semibold leading-relaxed truncate " + (task.completed ? "line-through text-muted-foreground" : "")}>{task.title}</p>
{task.dueAt && (<span className="block text-[11px] text-muted-foreground/60 font-medium mt-0.5">{"Due " + format(new Date(task.dueAt), "MMM d, yyyy · h:mm a")}</span>)}
</div>
<span className="text-[10px] font-semibold uppercase tracking-wide rounded-full bg-muted px-2 py-0.5 text-muted-foreground">Task</span>
</div>
))}
</>)}
</>
          )}
        </div>
      </ScrollArea>

      {openNote && (
        <NoteOverlay
          key={openNote.id}
          note={openNote}
          onClose={handleCloseOverlay}
          searchQuery={searchQuery}
        />
      )}
    </div>
  );
}
